import { Link } from "react-router-dom";
import { useEffect, useState, useRef } from "react";
import {
  Sparkles, Users, Leaf, Search, Camera, MapPin, BookHeart, Briefcase,
  Church, GraduationCap, TreePine, ChevronRight, ChevronLeft, Quote,
  ArrowRight, Zap,
} from "lucide-react";
import SeoHead from "@/components/SeoHead";

// -----------------------------------------------------------------------------
// /family-tree-guide — Ancestry-style onboarding landing for the Family Tree
// feature. Mirrors Ancestry.com's "Put the puzzle pieces together" structure
// but rewritten in Social Pulse's warmer, community-first voice:
//   • Hero + one-line promise + primary CTA
//   • 4-step "How it works" carousel (auto-advances every 5 s, tap to jump)
//   • 6-item "You'll discover..." grid
//   • Testimonial (placeholder — edit copy inline via the constant)
//   • "Trusted by our sibling sites" strip
//   • Final CTA + supportive footnote
// -----------------------------------------------------------------------------

const STEPS = [
  {
    icon: Sparkles,
    title: "Start with what you already know",
    body:
      "Every detail helps. Add yourself, your parents, and any grandparents you remember. Names, birthdays, hometowns — even a nickname or a single photo is a beautiful start.",
    accent: "#F59E0B",
  },
  {
    icon: Leaf,
    title: "Watch for a hint leaf",
    body:
      "As you build, Social Pulse quietly searches memorials, obituaries and other family trees for matches. When we find a possible ancestor, a soft leaf appears on their card.",
    accent: "#10B981",
  },
  {
    icon: Search,
    title: "Explore your hint",
    body:
      "Tap the leaf to see the discovery. It might be a photograph you've never seen, a village your great-grandmother was born in, or a distant cousin building their own tree.",
    accent: "#3B82F6",
  },
  {
    icon: Users,
    title: "Keep going — together",
    body:
      "Invite your parents, siblings, and cousins to co-edit the same tree. The more relatives who help, the richer the story becomes — and every branch is preserved forever.",
    accent: "#EC4899",
  },
];

const DISCOVERIES = [
  { icon: MapPin,          title: "Where your ancestors were born",     hint: "Kerala village · Ohio farm town · Punjabi grandmother's lane" },
  { icon: Camera,          title: "What they looked like",              hint: "Portraits from wedding albums, passports and family reunions" },
  { icon: GraduationCap,   title: "Where they went to school",          hint: "Village schools, colleges, and the year they graduated" },
  { icon: Church,          title: "Where they worshipped",              hint: "The parish, temple, gurdwara, or mosque that shaped them" },
  { icon: Briefcase,       title: "How they made a living",             hint: "Farmer, seamstress, engineer, cook — every trade preserved" },
  { icon: TreePine,        title: "The tree that ties it all together", hint: "One shared family tree, editable by every relative" },
];

const TESTIMONIAL = {
  quote:
    "I started with just my grandmother's name. Two weeks later my cousin in Toronto joined the tree, uploaded photos I'd never seen, and we found four generations we didn't know existed. This is where our family lives now.",
  author: "Maria (a real user someday soon)",
  meta: "Kottayam · Kerala · India",
};

const SIBLING_LOGOS = [
  { name: "Kerala.com",        href: "https://kerala.com" },
  { name: "FoodPulse",         href: "https://foodpulse.co" },
  { name: "IndianFamilyTree",  href: "https://indianfamilytree.com" },
  { name: "MyFamilyTree.care", href: "https://myfamilytree.care" },
];

export default function FamilyTreeGuide() {
  const [publicTreeCount, setPublicTreeCount] = useState(null);

  useEffect(() => {
    // Show a live count in the CTAs so visitors see the community is active.
    fetch("/api/family-trees?limit=1")
      .then((r) => r.ok ? r.json() : null)
      .then((d) => setPublicTreeCount(d?.total ?? d?.items?.length ?? null))
      .catch(() => {});
  }, []);

  return (
    <div className="bg-white text-neutral-900" data-testid="family-tree-guide-page">
      <SeoHead section="family_tree" url="https://socialpulse.com/family-tree-guide" />
      {/* ─────────── Hero ─────────── */}
      <section className="relative overflow-hidden">
        <HeroBackdrop />
        <div className="relative max-w-6xl mx-auto px-6 lg:px-10 py-24 lg:py-32">
          <div className="overline text-amber-700 mb-4" data-testid="ftg-eyebrow">
            SOCIAL PULSE FAMILY TREE
          </div>
          <h1
            className="font-display text-5xl md:text-6xl lg:text-7xl leading-[0.98] tracking-tight max-w-4xl"
            data-testid="ftg-hero-title"
          >
            The story of your family,
            <br />
            <span className="text-amber-700">stitched together with care.</span>
          </h1>
          <p className="mt-6 max-w-2xl text-lg md:text-xl leading-relaxed text-neutral-600" data-testid="ftg-hero-sub">
            Build a family tree that grows with every relative who joins.
            Preserve names, photos and stories forever — and let Social Pulse
            quietly connect you to ancestors, cousins, and villages you never
            knew existed.
          </p>
          <div className="mt-10 flex flex-wrap items-center gap-3" data-testid="ftg-hero-ctas">
            <Link
              to="/family-trees/new"
              className="wofy-btn-primary inline-flex items-center gap-2 !text-base !px-6 !py-3"
              data-testid="ftg-cta-primary"
            >
              Create your tree — free forever <ArrowRight className="w-4 h-4" />
            </Link>
            <Link
              to="/family"
              className="wofy-btn-ghost inline-flex items-center gap-2 !text-base !px-6 !py-3"
              data-testid="ftg-cta-browse"
            >
              Browse public family trees
              {publicTreeCount != null && (
                <span className="text-xs opacity-70">({publicTreeCount})</span>
              )}
            </Link>
          </div>
          <p className="mt-4 text-xs text-neutral-500" data-testid="ftg-fine-print">
            No credit card. Ever. Free forever for every family — supported by
            Social Pulse community members.
          </p>
        </div>
      </section>

      {/* ─────────── Getting started is easy — 4-step carousel ─────────── */}
      <StepsCarousel />

      {/* ─────────── There's so much to discover ─────────── */}
      <section className="max-w-6xl mx-auto px-6 lg:px-10 py-24 border-t border-neutral-200">
        <div className="overline text-amber-700 mb-2">SO MUCH TO DISCOVER</div>
        <h2 className="font-display text-4xl md:text-5xl tracking-tight max-w-3xl">
          Every ancestor was once a whole person, with a whole life.
        </h2>
        <p className="mt-4 text-lg text-neutral-600 max-w-2xl">
          As your tree grows, hints surface the pieces most families lose to
          time. Here's what you might uncover.
        </p>
        <div className="mt-12 grid md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="ftg-discover-grid">
          {DISCOVERIES.map((d, i) => (
            <div
              key={i}
              className="rounded-2xl border border-neutral-200 p-6 hover:border-amber-400 hover:shadow-sm transition-all"
              data-testid={`ftg-discover-${i}`}
            >
              <div
                className="w-11 h-11 rounded-xl flex items-center justify-center mb-4"
                style={{ background: "linear-gradient(135deg, #FEF3C7 0%, #FDE68A 100%)" }}
              >
                <d.icon className="w-5 h-5 text-amber-700" />
              </div>
              <div className="font-display text-xl leading-snug">{d.title}</div>
              <p className="mt-2 text-sm text-neutral-600 leading-relaxed">{d.hint}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ─────────── Testimonial ─────────── */}
      <section className="bg-neutral-50 border-y border-neutral-200 py-24">
        <div className="max-w-4xl mx-auto px-6 lg:px-10 text-center" data-testid="ftg-testimonial">
          <Quote className="w-8 h-8 text-amber-500 mx-auto opacity-70" />
          <blockquote className="mt-6 font-display text-2xl md:text-3xl leading-[1.35] text-neutral-800">
            "{TESTIMONIAL.quote}"
          </blockquote>
          <div className="mt-8 text-sm text-neutral-500">
            <div className="font-medium text-neutral-700">{TESTIMONIAL.author}</div>
            <div>{TESTIMONIAL.meta}</div>
          </div>
        </div>
      </section>

      {/* ─────────── Featured / Trusted-by strip ─────────── */}
      <section className="max-w-6xl mx-auto px-6 lg:px-10 py-16">
        <div className="text-center text-xs font-mono uppercase tracking-[0.22em] text-neutral-500 mb-8">
          Part of the Social Pulse sibling network
        </div>
        <div className="flex flex-wrap items-center justify-center gap-x-10 gap-y-6" data-testid="ftg-siblings">
          {SIBLING_LOGOS.map((s) => (
            <a
              key={s.name}
              href={s.href}
              target="_blank"
              rel="noreferrer"
              className="text-neutral-400 hover:text-neutral-800 transition-colors font-display text-xl"
              data-testid={`ftg-sibling-${s.name.toLowerCase().replace(/[^a-z]/g, "")}`}
            >
              {s.name}
            </a>
          ))}
        </div>
      </section>

      {/* ─────────── Final CTA ─────────── */}
      <section className="relative overflow-hidden">
        <FinalCtaBackdrop />
        <div className="relative max-w-4xl mx-auto px-6 lg:px-10 py-24 text-center">
          <div className="inline-flex items-center gap-2 text-xs font-mono uppercase tracking-widest text-amber-700 mb-4">
            <Zap className="w-3.5 h-3.5" /> IT TAKES 30 SECONDS
          </div>
          <h2 className="font-display text-4xl md:text-5xl tracking-tight text-neutral-900 leading-tight">
            Add your name. Watch the tree grow.
          </h2>
          <p className="mt-4 text-lg text-neutral-600 max-w-2xl mx-auto">
            Every family deserves a place where its story is safe forever.
            Social Pulse is that place — free, ad-free, and made for families
            who care.
          </p>
          <div className="mt-10 flex flex-wrap items-center justify-center gap-3">
            <Link
              to="/family-trees/new"
              className="wofy-btn-primary inline-flex items-center gap-2 !text-base !px-7 !py-3.5"
              data-testid="ftg-cta-final"
            >
              Create your family tree <ArrowRight className="w-4 h-4" />
            </Link>
            <Link
              to="/celebrate"
              className="wofy-btn-ghost !text-base !px-7 !py-3.5"
              data-testid="ftg-cta-memorials"
            >
              Or start with a memorial
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}

// ─────────────────────── Sub-components ───────────────────────

function StepsCarousel() {
  const [i, setI] = useState(0);
  const timer = useRef(null);

  useEffect(() => {
    if (timer.current) clearInterval(timer.current);
    timer.current = setInterval(() => setI((prev) => (prev + 1) % STEPS.length), 6000);
    return () => clearInterval(timer.current);
  }, []);

  const step = STEPS[i];
  const StepIcon = step.icon;

  return (
    <section className="max-w-6xl mx-auto px-6 lg:px-10 py-24 border-t border-neutral-200">
      <div className="overline text-amber-700 mb-2">GETTING STARTED IS EASY</div>
      <h2 className="font-display text-4xl md:text-5xl tracking-tight max-w-3xl">
        Four small steps. One lifelong tree.
      </h2>
      <div className="mt-12 grid lg:grid-cols-[1fr_auto] gap-10 items-start" data-testid="ftg-steps">
        <div>
          {/* Big highlighted step */}
          <div className="flex items-start gap-6">
            <div
              className="shrink-0 w-20 h-20 rounded-2xl flex items-center justify-center transition-colors"
              style={{
                background: `linear-gradient(135deg, ${step.accent}15 0%, ${step.accent}30 100%)`,
                border: `1px solid ${step.accent}40`,
              }}
            >
              <StepIcon className="w-8 h-8" style={{ color: step.accent }} />
            </div>
            <div>
              <div className="text-xs font-mono uppercase tracking-widest text-neutral-500">
                Step {i + 1} of {STEPS.length}
              </div>
              <div
                className="mt-1 font-display text-3xl md:text-4xl leading-tight"
                data-testid={`ftg-step-title-${i}`}
              >
                {step.title}
              </div>
              <p className="mt-4 text-lg text-neutral-600 leading-relaxed max-w-xl">
                {step.body}
              </p>
            </div>
          </div>
          {/* Arrows + dots */}
          <div className="mt-10 flex items-center gap-4">
            <button
              type="button"
              onClick={() => setI((prev) => (prev - 1 + STEPS.length) % STEPS.length)}
              data-testid="ftg-step-prev"
              className="w-10 h-10 rounded-full border border-neutral-300 flex items-center justify-center hover:bg-neutral-100"
              aria-label="Previous step"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <div className="flex items-center gap-2">
              {STEPS.map((_, idx) => (
                <button
                  key={idx}
                  type="button"
                  onClick={() => setI(idx)}
                  data-testid={`ftg-step-dot-${idx}`}
                  aria-label={`Go to step ${idx + 1}`}
                  className="h-1.5 rounded-full transition-all"
                  style={{
                    width: idx === i ? 32 : 8,
                    background: idx === i ? step.accent : "#D4D4D4",
                  }}
                />
              ))}
            </div>
            <button
              type="button"
              onClick={() => setI((prev) => (prev + 1) % STEPS.length)}
              data-testid="ftg-step-next"
              className="w-10 h-10 rounded-full border border-neutral-300 flex items-center justify-center hover:bg-neutral-100"
              aria-label="Next step"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Compact step list */}
        <ol className="hidden lg:block space-y-3 min-w-[260px]">
          {STEPS.map((s, idx) => {
            const active = idx === i;
            return (
              <li key={idx}>
                <button
                  type="button"
                  onClick={() => setI(idx)}
                  data-testid={`ftg-step-list-${idx}`}
                  className={`w-full text-left rounded-xl p-3 flex items-start gap-3 border transition-all ${
                    active ? "border-amber-400 bg-amber-50" : "border-neutral-200 hover:border-neutral-300"
                  }`}
                >
                  <span
                    className="w-8 h-8 rounded-full flex items-center justify-center text-sm font-mono shrink-0"
                    style={{
                      background: active ? s.accent : "#F5F5F5",
                      color: active ? "#fff" : "#525252",
                    }}
                  >
                    {idx + 1}
                  </span>
                  <span className="text-sm leading-snug pt-1.5">{s.title}</span>
                </button>
              </li>
            );
          })}
        </ol>
      </div>
    </section>
  );
}

function HeroBackdrop() {
  return (
    <div className="absolute inset-0 pointer-events-none">
      <div
        className="absolute inset-0"
        style={{
          background:
            "radial-gradient(ellipse 60% 60% at 20% 0%, #FEF3C7 0%, transparent 60%), " +
            "radial-gradient(ellipse 50% 50% at 100% 100%, #FCE7F3 0%, transparent 60%), " +
            "#FFFFFF",
        }}
      />
      {/* Subtle grid for texture */}
      <svg className="absolute inset-0 w-full h-full opacity-[0.03]" xmlns="http://www.w3.org/2000/svg">
        <defs>
          <pattern id="ftg-grid" width="40" height="40" patternUnits="userSpaceOnUse">
            <path d="M 40 0 L 0 0 0 40" fill="none" stroke="#000" strokeWidth="1" />
          </pattern>
        </defs>
        <rect width="100%" height="100%" fill="url(#ftg-grid)" />
      </svg>
      <div className="absolute -top-24 -right-24 w-96 h-96 rounded-full opacity-40 blur-3xl" style={{ background: "#FCD34D" }} />
      <div className="absolute -bottom-24 -left-24 w-96 h-96 rounded-full opacity-30 blur-3xl" style={{ background: "#F9A8D4" }} />
    </div>
  );
}

function FinalCtaBackdrop() {
  return (
    <div className="absolute inset-0 pointer-events-none">
      <div
        className="absolute inset-0"
        style={{
          background:
            "radial-gradient(ellipse 60% 60% at 50% 100%, #FEF3C7 0%, transparent 60%), #FFFFFF",
        }}
      />
    </div>
  );
}
